// SpectatorCrowd — 3D bleachers + a 75-strong seated crowd around the arena.
//
// Rendering strategy:
//   • Stands = a handful of static box meshes (each stand is a stepped
//     bleacher — 3 rows) that frame the current sport's footprint.
//   • Crowd  = one InstancedMesh of ~75 seated block-character bodies +
//     one InstancedMesh of arms so a "cheer" animation can lift every
//     spectator's arms in unison without per-instance draw cost.
//   • Idle animation: each instance gets a tiny per-frame Y sway based
//     on `sin(t + phase[i])` so the crowd looks alive without CPU cost.
//   • Cheer: on every new lastGoal, we bump `cheerUntilRef` and, while
//     active, raise the arms-mesh instances by ~0.35m and add a jump
//     bob to the body instances.
//   • Distance culling: the whole group is skipped when the camera is
//     more than ~30m from arena centre — keeps the 100+ instance
//     matrices out of the render list when the visitor is elsewhere.

import React, { useEffect, useMemo, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";

import {
  ARENA_CENTER,
  BASKETBALL_COURT,
  SOCCER_PITCH,
  FOOTBALL_FIELD,
} from "../arena/arenaState";

const CROWD_SIZE = 78;                 // total seated spectators
const CULL_DIST = 34;                  // metres — cull crowd + stands beyond this
const STAND_ROW_HEIGHT = 0.5;
const STAND_ROW_DEPTH = 0.9;
const STAND_ROWS = 3;
const JERSEY_PALETTE = [
  "#3b82f6", "#ef4444", "#22c55e", "#f59e0b",
  "#a855f7", "#f97316", "#eab308", "#0ea5e9",
];

function getFootprint(sport) {
  if (sport === "football") return { L: FOOTBALL_FIELD.length, W: FOOTBALL_FIELD.width };
  if (sport === "soccer")   return { L: SOCCER_PITCH.length, W: SOCCER_PITCH.width };
  return { L: BASKETBALL_COURT.length, W: BASKETBALL_COURT.width };
}

/** Build a bleacher stand from `rows` stacked steps. */
function BleacherStand({ x, z, rotY = 0, width = 12 }) {
  return (
    <group position={[x, 0, z]} rotation={[0, rotY, 0]}>
      {Array.from({ length: STAND_ROWS }).map((_, i) => (
        <mesh
          key={i}
          receiveShadow
          position={[0, i * STAND_ROW_HEIGHT + STAND_ROW_HEIGHT / 2, -i * STAND_ROW_DEPTH]}
        >
          <boxGeometry args={[width, STAND_ROW_HEIGHT, STAND_ROW_DEPTH]} />
          <meshStandardMaterial
            color={i % 2 === 0 ? "#1e293b" : "#334155"}
            roughness={0.85}
          />
        </mesh>
      ))}
      {/* Base */}
      <mesh position={[0, 0.1, -0.45]} receiveShadow>
        <boxGeometry args={[width, 0.2, STAND_ROWS * STAND_ROW_DEPTH]} />
        <meshStandardMaterial color="#0b1530" roughness={0.85} />
      </mesh>
    </group>
  );
}

export default function SpectatorCrowd({ sport, lastGoal, center }) {
  const groupRef = useRef();
  const bodyRef = useRef();
  const armsRef = useRef();
  const three = useThree();
  const cheerUntilRef = useRef(0);
  const scratch = useMemo(() => new THREE.Object3D(), []);
  const armScratch = useMemo(() => new THREE.Object3D(), []);
  const bodyColor = useMemo(() => new THREE.Color(), []);
  const armColor = useMemo(() => new THREE.Color(), []);

  const { L, W } = getFootprint(sport);

  // Per-instance random offsets so the crowd doesn't sway in perfect unison.
  const seats = useMemo(() => {
    const items = [];
    // Distribute along the perimeter of the current footprint, biased to the
    // two long sides (+/- Z). Soccer/football get all four sides; basketball
    // only long sides (courtside seats).
    const sides = sport === "basketball" ? ["+z", "-z"] : ["+z", "-z", "+x", "-x"];
    const perSide = Math.floor(CROWD_SIZE / sides.length);
    for (let sIdx = 0; sIdx < sides.length; sIdx++) {
      const side = sides[sIdx];
      for (let i = 0; i < perSide; i++) {
        // Deterministic sub-random spread along the side
        const t = (i + 0.5) / perSide;
        const jitter = (Math.sin(i * 12.9898 + sIdx * 78.233) * 43758.5453) % 1;
        let x, z, ry;
        // Seating row (0..STAND_ROWS-1)
        const row = i % STAND_ROWS;
        const rowY = row * STAND_ROW_HEIGHT + 0.62;
        const rowZOffset = row * STAND_ROW_DEPTH;
        if (side === "+z") {
          x = (t - 0.5) * (L - 1.5) + (jitter * 0.4 - 0.2);
          z = W / 2 + 1.2 + rowZOffset;
          ry = Math.PI; // facing -Z toward the field
        } else if (side === "-z") {
          x = (t - 0.5) * (L - 1.5) + (jitter * 0.4 - 0.2);
          z = -(W / 2 + 1.2 + rowZOffset);
          ry = 0; // facing +Z
        } else if (side === "+x") {
          x = L / 2 + 1.2 + rowZOffset;
          z = (t - 0.5) * (W - 1.5) + (jitter * 0.4 - 0.2);
          ry = -Math.PI / 2; // facing -X
        } else {
          x = -(L / 2 + 1.2 + rowZOffset);
          z = (t - 0.5) * (W - 1.5) + (jitter * 0.4 - 0.2);
          ry = Math.PI / 2; // facing +X
        }
        items.push({
          x,
          y: rowY,
          z,
          ry,
          phase: (i * 1.37 + sIdx * 2.11) % (Math.PI * 2),
          jersey: JERSEY_PALETTE[(i + sIdx * 3) % JERSEY_PALETTE.length],
        });
      }
    }
    return items;
  }, [sport, L, W]);

  const total = seats.length;

  // Cheer trigger on every new goal
  useEffect(() => {
    if (!lastGoal) return;
    cheerUntilRef.current = Date.now() + 1500;
  }, [lastGoal]);

  // Populate initial instance matrices + per-instance jersey colours
  useEffect(() => {
    if (!bodyRef.current || !armsRef.current) return;
    for (let i = 0; i < total; i++) {
      const s = seats[i];
      // Body
      scratch.position.set(s.x, s.y, s.z);
      scratch.rotation.set(0, s.ry, 0);
      scratch.updateMatrix();
      bodyRef.current.setMatrixAt(i, scratch.matrix);
      bodyColor.set(s.jersey);
      bodyRef.current.setColorAt(i, bodyColor);
      // Arms
      armScratch.position.set(s.x, s.y + 0.02, s.z);
      armScratch.rotation.set(0, s.ry, 0);
      armScratch.updateMatrix();
      armsRef.current.setMatrixAt(i, armScratch.matrix);
      armColor.set(s.jersey);
      armsRef.current.setColorAt(i, armColor);
    }
    bodyRef.current.instanceMatrix.needsUpdate = true;
    armsRef.current.instanceMatrix.needsUpdate = true;
    if (bodyRef.current.instanceColor) bodyRef.current.instanceColor.needsUpdate = true;
    if (armsRef.current.instanceColor) armsRef.current.instanceColor.needsUpdate = true;
  }, [seats, total, bodyColor, armColor, scratch, armScratch]);

  // Per-frame: distance cull + idle sway + cheer animation
  useFrame((state) => {
    if (!groupRef.current) return;
    const cam = three.camera.position;
    const cc = center || ARENA_CENTER;
    const dx = cam.x - cc[0];
    const dz = cam.z - cc[2];
    const distSq = dx * dx + dz * dz;
    const visible = distSq < CULL_DIST * CULL_DIST;
    groupRef.current.visible = visible;
    if (!visible) return;

    const t = state.clock.elapsedTime;
    const cheerActive = Date.now() < cheerUntilRef.current;

    if (!bodyRef.current || !armsRef.current) return;
    for (let i = 0; i < total; i++) {
      const s = seats[i];
      const sway = Math.sin(t * 1.6 + s.phase) * 0.05;
      const bounce = cheerActive ? Math.abs(Math.sin(t * 6 + s.phase)) * 0.35 : 0;
      scratch.position.set(s.x, s.y + sway + bounce, s.z);
      scratch.rotation.set(0, s.ry + sway * 0.3, 0);
      scratch.updateMatrix();
      bodyRef.current.setMatrixAt(i, scratch.matrix);
      // Arms: on cheer, lift by ~0.35m; idle position tracks body
      const armLift = cheerActive ? 0.35 + bounce * 0.4 : 0;
      armScratch.position.set(s.x, s.y + 0.02 + sway + bounce + armLift, s.z);
      armScratch.rotation.set(cheerActive ? -1.1 : 0, s.ry, 0);
      armScratch.updateMatrix();
      armsRef.current.setMatrixAt(i, armScratch.matrix);
    }
    bodyRef.current.instanceMatrix.needsUpdate = true;
    armsRef.current.instanceMatrix.needsUpdate = true;
  });

  // Build the stands to frame the current footprint.
  const stands = useMemo(() => {
    const [ax, , az] = center || ARENA_CENTER;
    const sides = sport === "basketball" ? ["+z", "-z"] : ["+z", "-z", "+x", "-x"];
    const arr = [];
    for (const side of sides) {
      if (side === "+z") arr.push({ x: ax, z: az + W / 2 + 1.0, rotY: Math.PI, w: L + 2 });
      else if (side === "-z") arr.push({ x: ax, z: az - (W / 2 + 1.0), rotY: 0, w: L + 2 });
      else if (side === "+x") arr.push({ x: ax + L / 2 + 1.0, z: az, rotY: -Math.PI / 2, w: W + 2 });
      else arr.push({ x: ax - (L / 2 + 1.0), z: az, rotY: Math.PI / 2, w: W + 2 });
    }
    return arr;
  }, [sport, L, W, center]);

  const [ax, , az] = center || ARENA_CENTER;

  return (
    <group ref={groupRef}>
      {/* Bleacher stands framing the field */}
      {stands.map((s, i) => (
        <BleacherStand key={i} x={s.x} z={s.z} rotY={s.rotY} width={s.w} />
      ))}

      {/* Instanced seated crowd — bodies. Seats are computed relative to
          the field footprint centre; we add the arena world offset here so
          each instance winds up at world (ax + s.x, s.y, az + s.z). */}
      <group position={[ax, 0, az]}>
        <instancedMesh
          ref={bodyRef}
          args={[undefined, undefined, total]}
          castShadow
          frustumCulled={false}
        >
          <boxGeometry args={[0.55, 0.7, 0.45]} />
          <meshStandardMaterial roughness={0.7} />
        </instancedMesh>

        {/* Instanced arms — lift together on cheer */}
        <instancedMesh
          ref={armsRef}
          args={[undefined, undefined, total]}
          frustumCulled={false}
        >
          <boxGeometry args={[0.6, 0.25, 0.2]} />
          <meshStandardMaterial roughness={0.7} />
        </instancedMesh>
      </group>
    </group>
  );
}
